import React, { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL;

function formatDay(dateValue) {
  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return "Zeitpunkt unbekannt";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Zeitpunkt unbekannt";
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function groupByCategory(entries) {
  return entries.reduce((groups, entry) => {
    const category = entry.category || "Sonstiges";
    if (!groups[category]) groups[category] = [];
    groups[category].push(entry);
    return groups;
  }, {});
}

export default function PatientDocumentationPanel({ patientId }) {
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [entries, setEntries] = useState([]);
  const [loadingDays, setLoadingDays] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;

    async function loadDays() {
      try {
        setLoadingDays(true);
        setError("");
        const res = await fetch(`${API}/patients/${patientId}/documentation/days`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!ignore) {
          setDays(Array.isArray(data) ? data : []);
          setSelectedDate((current) => current || data?.[0]?.date || "");
        }
      } catch (e) {
        if (!ignore) setError(e?.message || "Dokumentation konnte nicht geladen werden.");
      } finally {
        if (!ignore) setLoadingDays(false);
      }
    }

    loadDays();
    return () => {
      ignore = true;
    };
  }, [patientId]);

  useEffect(() => {
    if (!selectedDate) {
      setEntries([]);
      return;
    }

    let ignore = false;

    async function loadEntries() {
      try {
        setLoadingEntries(true);
        setError("");
        const res = await fetch(
          `${API}/patients/${patientId}/documentation?date=${encodeURIComponent(selectedDate)}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!ignore) setEntries(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!ignore) setError(e?.message || "Einträge konnten nicht geladen werden.");
      } finally {
        if (!ignore) setLoadingEntries(false);
      }
    }

    loadEntries();
    return () => {
      ignore = true;
    };
  }, [patientId, selectedDate]);

  const groupedEntries = useMemo(() => groupByCategory(entries), [entries]);
  const categoryNames = Object.keys(groupedEntries);

  return (
    <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[280px,1fr]">
      <aside className="rounded-lg border border-gray-200 bg-white/80 p-4">
        <h2 className="text-lg font-semibold text-gray-900">Dokumentation</h2>
        <p className="mt-1 text-sm text-gray-600">Tagesübersicht der Pflegeeinträge</p>

        <div className="mt-4 space-y-2">
          {loadingDays && <div className="text-sm text-gray-600">Tage werden geladen…</div>}
          {!loadingDays && days.length === 0 && (
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
              Noch keine Dokumentation vorhanden.
            </div>
          )}
          {days.map((day) => (
            <button
              key={day.date}
              type="button"
              onClick={() => setSelectedDate(day.date)}
              className={[
                "w-full rounded-md border px-3 py-2 text-left transition",
                selectedDate === day.date
                  ? "border-blue-500 bg-blue-50 text-blue-900"
                  : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50",
              ].join(" ")}
            >
              <div className="font-medium capitalize">{formatDay(day.date)}</div>
              <div className="mt-1 text-xs text-gray-500">
                {day.count} {day.count === 1 ? "Eintrag" : "Einträge"}
              </div>
            </button>
          ))}
        </div>
      </aside>

      <section className="min-h-[360px] rounded-lg border border-gray-200 bg-white/80 p-5">
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {!selectedDate && !loadingDays && (
          <div className="text-sm text-gray-600">Wähle einen Tag aus der Übersicht.</div>
        )}

        {selectedDate && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-3">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 capitalize">
                  {formatDay(selectedDate)}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Strukturierte Pflegeeinträge
                </p>
              </div>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
                {entries.length} {entries.length === 1 ? "Eintrag" : "Einträge"}
              </span>
            </div>

            {loadingEntries && (
              <div className="mt-5 text-sm text-gray-600">Einträge werden geladen…</div>
            )}

            {!loadingEntries && categoryNames.length === 0 && (
              <div className="mt-5 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
                Für diesen Tag wurden keine Einträge gefunden.
              </div>
            )}

            {!loadingEntries && categoryNames.length > 0 && (
              <div className="mt-5 space-y-5">
                {categoryNames.map((category) => (
                  <div key={category}>
                    <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                      {category}
                    </h4>
                    <div className="mt-2 space-y-3">
                      {groupedEntries[category].map((entry) => (
                        <article
                          key={entry.id}
                          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <h5 className="text-base font-semibold text-gray-900">
                                {entry.title}
                              </h5>
                              <p className="mt-1 text-sm text-gray-600">
                                {formatDateTime(entry.documented_at)}
                              </p>
                            </div>
                            {entry.review_required && (
                              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
                                Prüfen
                              </span>
                            )}
                          </div>

                          <p className="mt-3 text-sm leading-6 text-gray-800">
                            {entry.summary}
                          </p>

                          {Array.isArray(entry.details) && entry.details.length > 0 && (
                            <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                              {entry.details.map((detail, index) => (
                                <div
                                  key={`${entry.id}-${detail.label}-${index}`}
                                  className="rounded-md bg-gray-50 px-3 py-2"
                                >
                                  <dt className="text-xs font-medium text-gray-500">
                                    {detail.label}
                                  </dt>
                                  <dd className="mt-0.5 text-sm text-gray-900">
                                    {detail.value}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          )}
                        </article>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
